import { useState } from "react";
import { Alert, AlertTitle, Box, Link, Stack, Typography } from "@mui/material";
import { useInvoices, useUpdateInvoice } from "../../api/lists";
import { InvoiceFormDialog } from "../../components/Settings/InvoiceFormDialog";
import { DateDisplay } from "../../components/DateDisplay";
import type {
  InvoiceEntity,
  NewInvoiceEntity,
  UpdateInvoiceEntity,
} from "../../api/types";

/**
 * A missing invoice amount is legal, but easy to forget — surface invoices that
 * have no amount as a dashboard warning, each opening the invoice editor so the
 * amount can be filled in on the spot.
 */
export const InvoiceAmountWarnings = () => {
  const { data: invoices } = useInvoices();
  const updateInvoice = useUpdateInvoice();
  const [editing, setEditing] = useState<InvoiceEntity | null>(null);

  const missing = (invoices ?? []).filter((inv) => inv.amount == null);
  if (missing.length === 0) return null;

  // Throws on failure: the form shows the error itself.
  const handleSave = async (entity: NewInvoiceEntity) => {
    if (!editing) return;
    await updateInvoice.mutateAsync({
      id: editing.id,
      ...entity,
    } as UpdateInvoiceEntity);
    setEditing(null);
  };

  return (
    <>
      <Alert severity="warning" sx={{ mb: 2 }}>
        <AlertTitle>
          {missing.length === 1
            ? "1 invoice is missing an amount"
            : `${missing.length} invoices are missing an amount`}
        </AlertTitle>
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          {missing.map((inv) => (
            <Box key={inv.id}>
              <Link
                component="button"
                type="button"
                onClick={() => setEditing(inv)}
                underline="hover"
                sx={{ textAlign: "left" }}
              >
                {inv.name}
              </Link>
              <Typography
                component="span"
                variant="body2"
                sx={{ color: "text.secondary", ml: 1 }}
              >
                Due <DateDisplay date={inv.dueDate} />
              </Typography>
            </Box>
          ))}
        </Stack>
      </Alert>
      {editing && (
        <InvoiceFormDialog
          open
          title="Edit invoice"
          initialData={editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </>
  );
};
